import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  useTheme,
  Tooltip,
  CircularProgress,
  LinearProgress,
  InputAdornment,
} from "@mui/material";
import {
  Plus,
  Trash2,
  Upload,
  Search,
  X,
  CheckCircle,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";
import {
  fetchLILeads,
  createLILead,
  updateLILead,
  deleteLILead,
  importLILeads,
  validateLILeads,
  fetchLIICPs,
} from "../../../../../../redux/actions/linkedinOutreachAction";

const VALIDATION_STATUS_MAP = {
  validated: { color: "success", label: "Validated" },
  rejected: { color: "error", label: "Rejected" },
  pending: { color: "default", label: "Pending" },
  review: { color: "warning", label: "Review" },
};

const LeadList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { leads, icps, accounts, loading } = useSelector(
    (state) => state.linkedinOutreach
  );

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [icpFilter, setIcpFilter] = useState("");
  const [validationFilter, setValidationFilter] = useState("");
  const [leadStatusFilter, setLeadStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [addForm, setAddForm] = useState({
    linkedin_url: "",
    first_name: "",
    last_name: "",
    company: "",
    job_title: "",
    icp_id: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const [importUrls, setImportUrls] = useState("");
  const [importIcpId, setImportIcpId] = useState("");
  const [importAccountIds, setImportAccountIds] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [validating, setValidating] = useState(false);

  useEffect(() => {
    const params = { page: page + 1, page_size: 25 };
    if (search) params.search = search;
    if (icpFilter) params.icp_id = icpFilter;
    if (validationFilter) params.validation_status = validationFilter;
    if (leadStatusFilter) params.lead_status = leadStatusFilter;
    if (sourceFilter) params.source = sourceFilter;
    dispatch(fetchLILeads(params));
  }, [dispatch, page, search, icpFilter, validationFilter, leadStatusFilter, sourceFilter]);

  useEffect(() => {
    dispatch(fetchLIICPs());
  }, [dispatch]);

  const items = leads?.items || [];
  const total = leads?.total || 0;

  const handleAddLead = async () => {
    setSubmitting(true);
    try {
      await dispatch(createLILead(addForm));
      setAddOpen(false);
      setAddForm({
        linkedin_url: "",
        first_name: "",
        last_name: "",
        company: "",
        job_title: "",
        icp_id: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setImportResult(null);
    try {
      const urls = importUrls
        .split("\n")
        .map((u) => u.trim())
        .filter(Boolean);
      const payload = { urls };
      if (importIcpId) payload.icp_id = importIcpId;
      if (importAccountIds.length > 0) payload.account_ids = importAccountIds;
      const result = await dispatch(importLILeads(payload));
      setImportResult(result);
      // Refresh leads list after import
      dispatch(fetchLILeads({ page: page + 1, page_size: 25 }));
    } finally {
      setImporting(false);
    }
  };

  const handleValidateAll = async () => {
    setValidating(true);
    try {
      const pendingLeads = items
        .filter((l) => l.validation_status === "pending")
        .map((l) => l.id);
      if (pendingLeads.length > 0) {
        await dispatch(validateLILeads(pendingLeads));
        dispatch(fetchLILeads({ page: page + 1, page_size: 25 }));
      }
    } finally {
      setValidating(false);
    }
  };

  const handleRowClick = (lead) => {
    setSelectedLead(lead);
    setDetailOpen(true);
  };

  const handleDeleteLead = async () => {
    setDeleting(true);
    try {
      await dispatch(deleteLILead(deleteConfirm.id));
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const headCellSx = {
    fontWeight: 600,
    fontSize: "0.8rem",
    bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5",
  };

  const getValidationChip = (status) => {
    const config = VALIDATION_STATUS_MAP[status] || VALIDATION_STATUS_MAP.pending;
    return (
      <Chip
        label={config.label}
        size="small"
        color={config.color}
        sx={{ borderRadius: "6px", fontSize: "0.65rem", height: 20, fontWeight: 600 }}
      />
    );
  };

  const getSourceChip = (source) => {
    const colors = {
      import: "#8b5cf6",
      discovery: "#3b82f6",
      manual: "#6b7280",
      campaign: "#f59e0b",
    };
    const color = colors[source] || colors.manual;
    return (
      <Chip
        label={source || "manual"}
        size="small"
        sx={{
          borderRadius: "6px",
          fontSize: "0.6rem",
          height: 18,
          fontWeight: 600,
          bgcolor: alpha(color, 0.1),
          color: color,
        }}
      />
    );
  };

  const getLeadStatusChip = (status) => {
    if (!status) return null;
    const colors = {
      new: "#3b82f6",
      contacted: "#f59e0b",
      connected: "#10b981",
      replied: "#8b5cf6",
      qualified: "#059669",
      disqualified: "#ef4444",
    };
    const color = colors[status] || "#6b7280";
    return (
      <Chip
        label={status}
        size="small"
        sx={{
          borderRadius: "6px",
          fontSize: "0.6rem",
          height: 18,
          fontWeight: 600,
          bgcolor: alpha(color, 0.1),
          color: color,
        }}
      />
    );
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          Leads ({total})
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              validating ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <ShieldCheck size={16} />
              )
            }
            onClick={handleValidateAll}
            disabled={validating}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
          >
            {validating ? "Validating..." : "Validate"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Upload size={16} />}
            onClick={() => {
              setImportResult(null);
              setImportUrls("");
              setImportIcpId("");
              setImportAccountIds([]);
              setImportOpen(true);
            }}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={() => setAddOpen(true)}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
          >
            Add Lead
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          placeholder="Search name or company..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ width: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} color={theme.palette.text.secondary} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ width: 180 }}>
          <InputLabel>ICP</InputLabel>
          <Select
            value={icpFilter}
            onChange={(e) => {
              setIcpFilter(e.target.value);
              setPage(0);
            }}
            label="ICP"
          >
            <MenuItem value="">All ICPs</MenuItem>
            {(icps || []).map((icp) => (
              <MenuItem key={icp.id} value={icp.id}>
                {icp.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 160 }}>
          <InputLabel>Validation</InputLabel>
          <Select
            value={validationFilter}
            onChange={(e) => {
              setValidationFilter(e.target.value);
              setPage(0);
            }}
            label="Validation"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="validated">Validated</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="review">Review</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 160 }}>
          <InputLabel>Lead Status</InputLabel>
          <Select
            value={leadStatusFilter}
            onChange={(e) => {
              setLeadStatusFilter(e.target.value);
              setPage(0);
            }}
            label="Lead Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="new">New</MenuItem>
            <MenuItem value="contacted">Contacted</MenuItem>
            <MenuItem value="connected">Connected</MenuItem>
            <MenuItem value="replied">Replied</MenuItem>
            <MenuItem value="qualified">Qualified</MenuItem>
            <MenuItem value="disqualified">Disqualified</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 140 }}>
          <InputLabel>Source</InputLabel>
          <Select
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value);
              setPage(0);
            }}
            label="Source"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="manual">Manual</MenuItem>
            <MenuItem value="import">Import</MenuItem>
            <MenuItem value="discovery">Discovery</MenuItem>
            <MenuItem value="campaign">Campaign</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading && items.length > 0 && (
          <LinearProgress sx={{ height: 2 }} />
        )}
        <TableContainer sx={{ flex: 1, overflow: "auto", opacity: loading ? 0.5 : 1, transition: "opacity 0.2s" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Name</TableCell>
                <TableCell sx={headCellSx}>Company</TableCell>
                <TableCell sx={headCellSx}>Job Title</TableCell>
                <TableCell sx={headCellSx}>ICP</TableCell>
                <TableCell sx={headCellSx}>Source</TableCell>
                <TableCell sx={headCellSx}>Validation</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell sx={headCellSx}>Created</TableCell>
                <TableCell sx={headCellSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                    sx={{ py: 4, color: theme.palette.text.secondary }}
                  >
                    No leads found.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((lead) => (
                  <TableRow
                    key={lead.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(lead)}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {[lead.first_name, lead.last_name]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>{lead.company || "—"}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {lead.job_title || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {lead.icp_name ? (
                        <Chip
                          label={lead.icp_name}
                          size="small"
                          sx={{
                            borderRadius: "6px",
                            fontSize: "0.6rem",
                            height: 18,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{getSourceChip(lead.source)}</TableCell>
                    <TableCell>
                      {getValidationChip(lead.validation_status)}
                    </TableCell>
                    <TableCell>
                      {getLeadStatusChip(lead.lead_status)}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {lead.created_at
                          ? new Date(lead.created_at).toLocaleDateString()
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", gap: 0.5 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {lead.linkedin_url && (
                          <Tooltip title="Open LinkedIn">
                            <IconButton
                              size="small"
                              onClick={() =>
                                window.open(lead.linkedin_url, "_blank")
                              }
                            >
                              <ExternalLink size={14} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteConfirm(lead)}
                          >
                            <Trash2
                              size={14}
                              color={theme.palette.error?.main}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {total > 25 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        )}
      </Paper>

      {/* Add Lead Dialog */}
      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add Lead</DialogTitle>
        <DialogContent>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <TextField
              label="LinkedIn URL"
              value={addForm.linkedin_url}
              onChange={(e) =>
                setAddForm({ ...addForm, linkedin_url: e.target.value })
              }
              fullWidth
              size="small"
              placeholder="https://www.linkedin.com/in/..."
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="First Name"
                value={addForm.first_name}
                onChange={(e) =>
                  setAddForm({ ...addForm, first_name: e.target.value })
                }
                fullWidth
                size="small"
              />
              <TextField
                label="Last Name"
                value={addForm.last_name}
                onChange={(e) =>
                  setAddForm({ ...addForm, last_name: e.target.value })
                }
                fullWidth
                size="small"
              />
            </Box>
            <TextField
              label="Company"
              value={addForm.company}
              onChange={(e) =>
                setAddForm({ ...addForm, company: e.target.value })
              }
              fullWidth
              size="small"
            />
            <TextField
              label="Job Title"
              value={addForm.job_title}
              onChange={(e) =>
                setAddForm({ ...addForm, job_title: e.target.value })
              }
              fullWidth
              size="small"
            />
            <FormControl size="small" fullWidth>
              <InputLabel>ICP</InputLabel>
              <Select
                value={addForm.icp_id}
                onChange={(e) =>
                  setAddForm({ ...addForm, icp_id: e.target.value })
                }
                label="ICP"
              >
                <MenuItem value="">None</MenuItem>
                {(icps || []).map((icp) => (
                  <MenuItem key={icp.id} value={icp.id}>
                    {icp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddLead}
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {submitting ? "Adding..." : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Import Leads
          <IconButton onClick={() => setImportOpen(false)} size="small">
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <TextField
              label="LinkedIn URLs (one per line)"
              value={importUrls}
              onChange={(e) => setImportUrls(e.target.value)}
              fullWidth
              multiline
              rows={8}
              size="small"
              placeholder={
                "https://www.linkedin.com/in/john-doe\nhttps://www.linkedin.com/in/jane-smith"
              }
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: "0.85rem",
                  fontFamily: "monospace",
                },
              }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>ICP (optional)</InputLabel>
              <Select
                value={importIcpId}
                onChange={(e) => setImportIcpId(e.target.value)}
                label="ICP (optional)"
              >
                <MenuItem value="">None</MenuItem>
                {(icps || []).map((icp) => (
                  <MenuItem key={icp.id} value={icp.id}>
                    {icp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Accounts (optional)</InputLabel>
              <Select
                multiple
                value={importAccountIds}
                onChange={(e) => setImportAccountIds(e.target.value)}
                label="Accounts (optional)"
                renderValue={(sel) =>
                  sel
                    .map(
                      (id) =>
                        (accounts || []).find((a) => a.id === id)?.name || id
                    )
                    .join(", ")
                }
              >
                {(accounts || []).map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>
                    {acc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {importResult && (
              <Box
                sx={{
                  bgcolor: alpha("#10b981", 0.08),
                  borderRadius: "8px",
                  p: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  Import Result
                </Typography>
                <Box sx={{ display: "flex", gap: 3 }}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Imported
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {importResult.imported ?? 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Duplicates
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {importResult.duplicates ?? 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Errors
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {importResult.errors ?? 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setImportOpen(false)}>
            {importResult ? "Close" : "Cancel"}
          </Button>
          {!importResult && (
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={!importUrls.trim() || importing}
              startIcon={
                importing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Upload size={16} />
                )
              }
            >
              {importing ? "Importing..." : "Import & Enrich"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Lead Details
          <IconButton onClick={() => setDetailOpen(false)} size="small">
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedLead && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              {/* Name + Avatar */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    flexShrink: 0,
                  }}
                >
                  {(
                    selectedLead.first_name?.[0] ||
                    selectedLead.last_name?.[0] ||
                    "?"
                  ).toUpperCase()}
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {[selectedLead.first_name, selectedLead.last_name]
                      .filter(Boolean)
                      .join(" ") || "—"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {selectedLead.job_title || "—"}
                    {selectedLead.company
                      ? ` at ${selectedLead.company}`
                      : ""}
                  </Typography>
                </Box>
              </Box>

              {/* Status Chips */}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {getValidationChip(selectedLead.validation_status)}
                {getLeadStatusChip(selectedLead.lead_status)}
                {getSourceChip(selectedLead.source)}
                {selectedLead.icp_name && (
                  <Chip
                    label={selectedLead.icp_name}
                    size="small"
                    sx={{
                      borderRadius: "6px",
                      fontSize: "0.65rem",
                      height: 20,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                    }}
                  />
                )}
              </Box>

              {/* Details Grid */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  mt: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    LinkedIn URL
                  </Typography>
                  {selectedLead.linkedin_url ? (
                    <Typography
                      variant="body2"
                      component="a"
                      href={selectedLead.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: theme.palette.primary.main,
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Profile <ExternalLink size={12} />
                    </Typography>
                  ) : (
                    <Typography variant="body2">—</Typography>
                  )}
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    Company
                  </Typography>
                  <Typography variant="body2">
                    {selectedLead.company || "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    Job Title
                  </Typography>
                  <Typography variant="body2">
                    {selectedLead.job_title || "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {selectedLead.created_at
                      ? new Date(selectedLead.created_at).toLocaleString()
                      : "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}>
                    Email
                  </Typography>
                  <Typography variant="body2">
                    {selectedLead.email ? (
                      <a href={`mailto:${selectedLead.email}`} style={{ color: theme.palette.primary.main, textDecoration: "none" }}>
                        {selectedLead.email}
                      </a>
                    ) : "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}>
                    Phone
                  </Typography>
                  <Typography variant="body2">
                    {selectedLead.phone || "—"}
                  </Typography>
                </Box>
              </Box>

              {/* Validation Reason */}
              {selectedLead.validation_reason && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Validation Reason
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      bgcolor: alpha(theme.palette.divider, 0.3),
                      borderRadius: "8px",
                      p: 1.5,
                      fontSize: "0.8rem",
                    }}
                  >
                    {selectedLead.validation_reason}
                  </Typography>
                </Box>
              )}

              {/* Profile Data */}
              {selectedLead.profile_data &&
                Object.keys(selectedLead.profile_data).length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        fontSize: "0.65rem",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      Profile Data
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        bgcolor: alpha(theme.palette.divider, 0.15),
                        borderRadius: "8px",
                        p: 1.5,
                      }}
                    >
                      {Object.entries(selectedLead.profile_data).map(
                        ([key, value]) => (
                          <Box
                            key={key}
                            sx={{ display: "flex", gap: 1 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                minWidth: 120,
                                color: theme.palette.text.secondary,
                              }}
                            >
                              {key
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase())}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.8rem" }}
                            >
                              {typeof value === "object"
                                ? JSON.stringify(value)
                                : String(value)}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  </Box>
                )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Lead</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete{" "}
            <strong>
              {[deleteConfirm?.first_name, deleteConfirm?.last_name]
                .filter(Boolean)
                .join(" ") || "this lead"}
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteLead}
            disabled={deleting}
            startIcon={
              deleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadList;
